import { useEffect, useRef, useState } from "react";

import Link from "next/link";

import { Anchor } from "@newsela/angelou";
import PropTypes from "prop-types";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE } from "@/components/ContentPromotions/constants";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { tab, ulWrapperCss, tabActive } from "./YourContentNav.style";

const propTypes = {
  selector: PropTypes.string,
};

const savedByYouLink =
  "/your-content/?saved-by-you=&content_type=allcontenttypes";
const createdByYouLink = "/your-content/?created-by-you";
const atYourSchoolLink = "/your-content/?at-your-school";
const promotedAtYourDistrictLink = PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.district;
const promotedAtYourSchoolLink = PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.school;

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const YourContentNav = ({ selector }) => {
  const { data: user } = useUser();
  const { isStudent } = getUserProperties(user);
  const savedByYouRef = useRef(null);
  const createdByYouRef = useRef(null);
  const atYourSchoolRef = useRef(null);
  const promotedAtYourDistrictRef = useRef(null);
  const promotedAtYourSchoolRef = useRef(null);
  const [activeTab, setActiveTab] = useState();
  const selectorAndRefObj = {
    "saved-by-you": savedByYouRef,
    "created-by-you": createdByYouRef,
    "at-your-school": atYourSchoolRef,
    "promoted-at-your-district": promotedAtYourDistrictRef,
    "promoted-at-your-school": promotedAtYourSchoolRef,
  };

  useEffect(() => {
    setActiveTab(selectorAndRefObj[selector]);
  }, [selector]);

  const isSavedByYouActive = activeTab === selectorAndRefObj["saved-by-you"];
  const isCreatedByYouActive =
    activeTab === selectorAndRefObj["created-by-you"];
  const isAtYourSchoolActive =
    activeTab === selectorAndRefObj["at-your-school"];
  const isPromotedAtYourDistrictActive =
    activeTab === selectorAndRefObj["promoted-at-your-district"];
  const isPromotedAtYourSchoolActive =
    activeTab === selectorAndRefObj["promoted-at-your-school"];

  const sharedAnchorTracking = {
    objectType: objectTypes.content,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
  };

  const renderSavedByYouLink = () => {
    return (
      <Link href={savedByYouLink} passHref legacyBehavior>
        <AnchorWithTrackEvent
          {...sharedAnchorTracking}
          ariaProps={{
            "aria-current": isSavedByYouActive ? "page" : null,
          }}
          __cssFor={{ root: isSavedByYouActive ? tabActive : tab }}
          ref={savedByYouRef}
          onFocus={() => {
            savedByYouRef?.current?.scrollIntoView(false);
          }}
        >
          Saved by you
        </AnchorWithTrackEvent>
      </Link>
    );
  };

  const renderCreatedByYouLink = () => {
    if (isStudent) {
      return <></>;
    }
    return (
      <Link href={createdByYouLink} passHref legacyBehavior>
        <AnchorWithTrackEvent
          {...sharedAnchorTracking}
          ariaProps={{
            "aria-current": isCreatedByYouActive ? "page" : null,
          }}
          __cssFor={{ root: isCreatedByYouActive ? tabActive : tab }}
          ref={createdByYouRef}
          onFocus={() => {
            createdByYouRef?.current?.scrollIntoView(false);
          }}
        >
          Created by you
        </AnchorWithTrackEvent>
      </Link>
    );
  };

  const renderPromotedAtYourDistrictLink = () => {
    return (
      <Link href={promotedAtYourDistrictLink} passHref legacyBehavior>
        <AnchorWithTrackEvent
          {...sharedAnchorTracking}
          ariaProps={{
            "aria-current": isPromotedAtYourDistrictActive ? "page" : null,
          }}
          dataProps={{
            "data-qa-selector": "promoted-at-your-district-nav-button",
          }}
          __cssFor={{ root: isPromotedAtYourDistrictActive ? tabActive : tab }}
          ref={promotedAtYourDistrictRef}
          onFocus={() => {
            promotedAtYourDistrictRef?.current?.scrollIntoView(false);
          }}
        >
          Promoted at your district
        </AnchorWithTrackEvent>
      </Link>
    );
  };

  const renderPromotedAtYourSchoolLink = () => {
    return (
      <Link href={promotedAtYourSchoolLink} passHref legacyBehavior>
        <AnchorWithTrackEvent
          {...sharedAnchorTracking}
          ariaProps={{
            "aria-current": isPromotedAtYourSchoolActive ? "page" : null,
          }}
          dataProps={{
            "data-qa-selector": "promoted-at-your-school-nav-button",
          }}
          __cssFor={{ root: isPromotedAtYourSchoolActive ? tabActive : tab }}
          ref={promotedAtYourSchoolRef}
          onFocus={() => {
            promotedAtYourSchoolRef?.current?.scrollIntoView(false);
          }}
        >
          Promoted at your school
        </AnchorWithTrackEvent>
      </Link>
    );
  };

  const renderAtYourSchoolLink = () => {
    if (isStudent) {
      return <></>;
    }
    return (
      <Link href={atYourSchoolLink} passHref legacyBehavior>
        <AnchorWithTrackEvent
          {...sharedAnchorTracking}
          ariaProps={{
            "aria-current": isAtYourSchoolActive ? "page" : null,
          }}
          __cssFor={{ root: isAtYourSchoolActive ? tabActive : tab }}
          ref={atYourSchoolRef}
          onFocus={() => {
            atYourSchoolRef?.current?.scrollIntoView(false);
          }}
        >
          School text sets
        </AnchorWithTrackEvent>
      </Link>
    );
  };

  return (
    <nav aria-label="Your content">
      <ul css={ulWrapperCss}>
        <li>{renderSavedByYouLink()}</li>
        <li>{renderPromotedAtYourDistrictLink()}</li>
        <li>{renderPromotedAtYourSchoolLink()}</li>
        <li>{renderCreatedByYouLink()}</li>
        <li>{renderAtYourSchoolLink()}</li>
      </ul>
    </nav>
  );
};

YourContentNav.propTypes = propTypes;

export default YourContentNav;
